import { useEffect, useId, useRef, useState } from 'react';
import { Bluetooth, ChevronDown, MapPin, QrCode, SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PointType } from '../../helpers/locationBrowse';

const TYPE_OPTIONS = [
    { type: 'wireless', label: 'Bezprzewodowy', Icon: Bluetooth },
    { type: 'gps', label: 'GPS', Icon: MapPin },
    { type: 'qrNfc', label: 'QR/NFC', Icon: QrCode },
] as const;

interface LocationTypeFilterProps {
    selectedTypes: readonly PointType[];
    onToggle: (type: PointType) => void;
}

export const LocationTypeFilter = ({ selectedTypes, onToggle }: LocationTypeFilterProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const optionsId = useId();

    useEffect(() => {
        if (!open) return;
        const closeOutside = (event: PointerEvent) => {
            const root = rootRef.current;
            if (!root || !(event.target instanceof Node) || root.contains(event.target)) return;
            if (root.contains(document.activeElement)) buttonRef.current?.focus();
            setOpen(false);
        };
        const closeOnFocusOutside = (event: FocusEvent) => {
            if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener('pointerdown', closeOutside);
        document.addEventListener('focusin', closeOnFocusOutside);
        return () => {
            document.removeEventListener('pointerdown', closeOutside);
            document.removeEventListener('focusin', closeOnFocusOutside);
        };
    }, [open]);

    return <div className="location-types-disclosure" ref={rootRef}
        onKeyDown={event => {
            if (event.key === 'Escape' && open) {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
                buttonRef.current?.focus();
            }
        }}>
        <button type="button" ref={buttonRef} className="location-types-toggle" aria-expanded={open}
            aria-controls={optionsId} onClick={() => setOpen(value => !value)}>
            <SlidersHorizontal aria-hidden="true" />
            <span>{t('Typy punktów')}</span>
            <ChevronDown className="location-types-chevron" aria-hidden="true" />
        </button>
        <fieldset id={optionsId} className="location-types" hidden={!open}>
            <legend className="sr-only">{t('Typy punktów')}</legend>
            <div className="location-type-options">
                {TYPE_OPTIONS.map(({ type, label, Icon }) => (
                    <label className={`location-type${selectedTypes.includes(type) ? ' is-selected' : ''}`} key={type}>
                        <input type="checkbox" checked={selectedTypes.includes(type)} onChange={() => onToggle(type)} />
                        <Icon aria-hidden="true" />
                        <span>{t(label)}</span>
                    </label>
                ))}
            </div>
        </fieldset>
    </div>;
};
